import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { FunnelSection } from '../../types/section.types';
import { useFunnelStore } from '../../store/funnelStore';
import CanvasColumn from './CanvasColumn';

interface CanvasSectionProps {
  section: FunnelSection;
}

/**
 * Canvas Section Component
 * Renders a section on the canvas with its columns
 */
const CanvasSection: React.FC<CanvasSectionProps> = ({ section }) => {
  const { selectedSectionId, selectSection, isPreviewMode } = useFunnelStore();
  const isSelected = selectedSectionId === section.id;

  const { setNodeRef, isOver } = useDroppable({
    id: `section-${section.id}`,
    data: { type: 'section', sectionId: section.id },
  });

  const { backgroundColor, padding, margin, maxWidth, minHeight, backgroundImage } = section.settings;

  // Build section styles
  const sectionStyles: React.CSSProperties = {
    backgroundColor: backgroundColor || '#ffffff',
    paddingTop: `${padding?.top || 60}px`,
    paddingRight: `${padding?.right || 20}px`,
    paddingBottom: `${padding?.bottom || 60}px`,
    paddingLeft: `${padding?.left || 20}px`,
    marginTop: `${margin?.top || 0}px`,
    marginRight: `${margin?.right || 0}px`,
    marginBottom: `${margin?.bottom || 0}px`,
    marginLeft: `${margin?.left || 0}px`,
    minHeight: minHeight || 'auto',
    backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
    backgroundSize: section.settings.backgroundSize || 'cover',
    backgroundPosition: section.settings.backgroundPosition || 'center',
    backgroundRepeat: section.settings.backgroundRepeat || 'no-repeat',
    position: 'relative',
  };

  const [isEditingName, setIsEditingName] = React.useState(false);
  const [editedName, setEditedName] = React.useState(section.name);
  const { updateSection } = useFunnelStore();

  const handleClick = (e: React.MouseEvent) => {
    // Don't handle clicks in preview mode
    if (isPreviewMode) return;
    
    // Only select section if clicking on the section itself, not on elements
    if (e.target === e.currentTarget || (e.target as HTMLElement).closest('.canvas-section-container')) {
      selectSection(section.id);
    }
  };

  const handleNameSave = () => {
    if (editedName.trim() && editedName !== section.name) {
      updateSection(section.id, { name: editedName.trim() });
    }
    setIsEditingName(false);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameSave();
    } else if (e.key === 'Escape') {
      setEditedName(section.name);
      setIsEditingName(false);
    }
  };

  return (
    <section
      ref={setNodeRef}
      style={sectionStyles}
      className={`
        canvas-section transition-all relative
        ${!isPreviewMode && isSelected ? 'ring-2 ring-blue-500 ring-inset' : ''}
        ${!isPreviewMode && isOver ? 'bg-primary-50 bg-opacity-50' : ''}
      `}
      onClick={handleClick}
    >
      {/* Section Header - Only visible in edit mode */}
      {!isPreviewMode && (
        <div className={`
          flex items-center justify-between px-4 py-2 border-b-2 border-dashed
          ${isSelected ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 border-gray-300'}
          transition-all
        `}>
        <div className="flex items-center gap-2">
          {isEditingName ? (
            <input
              type="text"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onBlur={handleNameSave}
              onKeyDown={handleNameKeyDown}
              autoFocus
              className="px-2 py-1 text-sm font-semibold border-2 border-blue-500 rounded focus:outline-none"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <>
              <span className={`text-sm font-semibold ${isSelected ? 'text-blue-700' : 'text-gray-700'}`}>
                📑 {section.name}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditingName(true);
                }}
                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded transition-all"
                title="Edit section name"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            </>
          )}
        </div>
        
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>{section.type}</span>
          <span>•</span>
          <span>{section.columns.length} column{section.columns.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
      )}

      {/* Container */}
      <div
        className="canvas-section-container mx-auto"
        style={{ maxWidth: maxWidth || '1200px' }}
      >
        {/* Columns Grid */}
        <div className="flex flex-wrap gap-4 p-4">
          {section.columns.map((column) => (
            <CanvasColumn
              key={column.id}
              column={column}
              sectionId={section.id}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CanvasSection;

